/**
 * GA4 event helpers.
 *
 * The GA4 base tag (G-NQEENZJWDT) is loaded once in index.html. These helpers
 * only push additional events to the existing dataLayer — they never add a
 * second tag or re-configure page-view tracking.
 *
 * IMPORTANT: No personally identifiable information (PII) is ever sent.
 * Names, emails, phone numbers, message text and company names are excluded.
 * If `window.gtag` is unavailable, every helper fails silently.
 */

type GtagCommand = "event" | "config" | "js" | "set";
type GtagParams = Record<string, unknown>;

declare global {
  interface Window {
    gtag?: (command: GtagCommand, target: string, params?: GtagParams) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Low-level wrapper. Returns true if the event was dispatched, false if gtag
 * was unavailable (so callers can decide whether to log).
 */
function trackEvent(eventName: string, params: GtagParams = {}): boolean {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }
  try {
    window.gtag("event", eventName, params);
    return true;
  } catch {
    return false;
  }
}

/** Fire once per confirmed successful Contact form submission. */
export function trackGenerateLead(source: string = "contact_form"): boolean {
  return trackEvent("generate_lead", {
    lead_source: source,
    page_path: typeof window !== "undefined" ? window.location.pathname : "/",
  });
}

/** Telephone link clicks. */
export function trackPhoneClick(phoneHref: string): boolean {
  // Only the tel: prefix is sent — never the full number is parsed out.
  return trackEvent("phone_click", {
    link_url: phoneHref,
    page_path: typeof window !== "undefined" ? window.location.pathname : "/",
  });
}

/** WhatsApp link clicks. */
export function trackWhatsAppClick(linkUrl: string): boolean {
  return trackEvent("whatsapp_click", {
    link_url: linkUrl,
    page_path: typeof window !== "undefined" ? window.location.pathname : "/",
  });
}

/** Email link clicks. */
export function trackEmailClick(emailHref: string): boolean {
  // Send only the mailto: href as-is (address is not parsed into a param).
  return trackEvent("email_click", {
    link_url: emailHref,
    page_path: typeof window !== "undefined" ? window.location.pathname : "/",
  });
}

/** Portfolio / file download clicks. */
export function trackFileDownload(fileName: string, linkUrl: string): boolean {
  return trackEvent("file_download", {
    file_name: fileName,
    link_url: linkUrl,
    page_path: typeof window !== "undefined" ? window.location.pathname : "/",
  });
}

/** Job application intent (email/WhatsApp apply links on Careers). */
export function trackJobApplication(
  role: string,
  method: "email" | "whatsapp",
): boolean {
  return trackEvent("job_application", {
    job_role: role,
    application_method: method,
    page_path: typeof window !== "undefined" ? window.location.pathname : "/",
  });
}
